import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { useTranslation } from 'react-i18next';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/Layout/AdminLayout';
import ClientLayout from './components/Layout/ClientLayout';
import UserLayout from './components/Layout/UserLayout';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import Setup from './pages/Setup';
import AdminDashboard from './pages/admin/Dashboard';
import Servers from './pages/admin/Servers';
import Clients from './pages/admin/Clients';
import SetupRequests from './pages/admin/SetupRequests';
import ClientDashboard from './pages/client/Dashboard';
import Users from './pages/client/Users';
import Desktop from './pages/user/Desktop';

const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
  key: 'muiltr',
});

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  useEffect(() => {
    document.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRtl]);

  const theme = useMemo(() => createTheme({
    direction: isRtl ? 'rtl' : 'ltr',
    palette: {
      primary: {
        main: '#1a237e',
        light: '#534bae',
        dark: '#000051',
      },
      secondary: {
        main: '#0d47a1',
        light: '#5472d3',
        dark: '#002171',
      },
      background: {
        default: '#f5f7fa',
      },
    },
    typography: {
      fontFamily: '"Cairo", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h3: {
        fontWeight: 700,
      },
      h4: {
        fontWeight: 700,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
    },
  }), [isRtl]);

  return (
    <CacheProvider value={isRtl ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/manage" element={<AdminLogin />} />
          <Route path="/setup" element={<Setup />} />

          <Route element={<ProtectedRoute requiredRole="ADMIN" />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/servers" element={<Servers />} />
              <Route path="/admin/clients" element={<Clients />} />
              <Route path="/admin/setup-requests" element={<SetupRequests />} />
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute requiredRole="CLIENT" />}>
            <Route element={<ClientLayout />}>
              <Route path="/client/dashboard" element={<ClientDashboard />} />
              <Route path="/client/users" element={<Users />} />
              <Route path="/client" element={<Navigate to="/client/dashboard" replace />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute requiredRole="USER" />}>
            <Route element={<UserLayout />}>
              <Route path="/user/desktop" element={<Desktop />} />
              <Route path="/user" element={<Navigate to="/user/desktop" replace />} />
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Router>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default App;
